import {
  AddonCardStats,
  AddonSource,
  AdminStatsStore,
  AdminStoreCollection,
  AllStatsResponse,
  AllTrendsResponse,
  CreateAdminStoreCollection,
  Route,
  TimeUnit,
  populateRoute,
} from "@repo/common"
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

const sortedIds = (storeIds: string[]): string[] => [...storeIds].sort()

const buildStoreParams = (
  storeIds: string[],
  includeZeroValueJobs: boolean,
): Record<string, string> => ({
  storeIds: storeIds.join(","),
  ...(includeZeroValueJobs && { includeZeroValueJobs: "true" }),
})

export const adminStatsQueries = {
  all: () => ["admin", "stats"],
  stores: () =>
    queryOptions({
      queryKey: [...adminStatsQueries.all(), "stores"],
      queryFn: () =>
        request<AdminStatsStore[]>({
          url: Route.STATS__ADMIN_STORES,
          method: "GET",
        }),
      staleTime: Number.POSITIVE_INFINITY,
    }),
  storeCollections: () =>
    queryOptions({
      queryKey: [...adminStatsQueries.all(), "store-collections"],
      queryFn: () =>
        request<AdminStoreCollection[]>({
          url: Route.STATS__ADMIN_STORE_COLLECTIONS,
          method: "GET",
        }),
    }),
  stats: (
    storeIds: string[],
    startDate: Date,
    endDate: Date,
    includeZeroValueJobs: boolean,
  ) =>
    queryOptions({
      queryKey: [
        ...adminStatsQueries.all(),
        "combined",
        sortedIds(storeIds),
        startDate.toISOString(),
        endDate.toISOString(),
        includeZeroValueJobs,
      ],
      queryFn: () =>
        request<AllStatsResponse>({
          url: populateRoute(
            Route.STATS__ADMIN_ALL,
            startDate.toISOString(),
            endDate.toISOString(),
          ),
          method: "GET",
          params: buildStoreParams(storeIds, includeZeroValueJobs),
        }),
      enabled: storeIds.length > 0,
      placeholderData: keepPreviousData,
    }),
  addonCard: (
    storeIds: string[],
    startDate: Date,
    endDate: Date,
    source: AddonSource,
    cardFilter: { toggledActionKeys: string[] },
    includeZeroValueJobs: boolean,
  ) =>
    queryOptions({
      queryKey: [
        ...adminStatsQueries.all(),
        "addon-card",
        sortedIds(storeIds),
        startDate.toISOString(),
        endDate.toISOString(),
        source,
        includeZeroValueJobs,
        ...cardFilter.toggledActionKeys,
      ],
      queryFn: () =>
        request<AddonCardStats>({
          url: populateRoute(
            Route.STATS__ADMIN_ADDON_CARD,
            startDate.toISOString(),
            endDate.toISOString(),
          ),
          method: "GET",
          params: {
            ...buildStoreParams(storeIds, includeZeroValueJobs),
            source,
            toggledActionKeys: JSON.stringify(cardFilter.toggledActionKeys),
          },
        }),
      placeholderData: keepPreviousData,
    }),
  trends: (
    storeIds: string[],
    timeUnit: TimeUnit,
    spanParams: Record<string, string>,
    endDate: Date,
    includeZeroValueJobs: boolean,
  ) =>
    queryOptions({
      queryKey: [
        ...adminStatsQueries.all(),
        "trends",
        sortedIds(storeIds),
        timeUnit,
        spanParams,
        endDate.toISOString(),
        includeZeroValueJobs,
      ],
      queryFn: () =>
        request<AllTrendsResponse>({
          url: populateRoute(
            Route.TRENDS__ADMIN_ALL,
            timeUnit,
            endDate.toISOString(),
          ),
          method: "GET",
          params: {
            ...buildStoreParams(storeIds, includeZeroValueJobs),
            ...spanParams,
          },
        }),
      enabled: storeIds.length > 0,
    }),
}

export function useStoreCollectionMutations() {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries({
      queryKey: adminStatsQueries.storeCollections().queryKey,
    })

  const createMutation = useMutation({
    mutationFn: (data: CreateAdminStoreCollection) =>
      request<AdminStoreCollection>({
        url: Route.STATS__ADMIN_STORE_COLLECTIONS,
        method: "POST",
        data,
      }),
    onSettled: invalidateQueries,
  })

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      request<void>({
        url: populateRoute(Route.STATS__ADMIN_STORE_COLLECTION, id),
        method: "PATCH",
        data: { name },
      }),
    onSettled: invalidateQueries,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      request<void>({
        url: populateRoute(Route.STATS__ADMIN_STORE_COLLECTION, id),
        method: "DELETE",
      }),
    onSettled: invalidateQueries,
  })

  return { createMutation, renameMutation, deleteMutation }
}
